import { useEffect, useMemo } from "react";
import { ListingApplicationState, ListingRankingApplicationsDocument, SortOrder,
  type ListingRankingApplicationsQuery } from "@/graphql/generated/operations";
import { usePagedQuery } from "@/lib/use-paged-query";
import type { OvertakingApplicationState } from "@/components/match/auto-match/auto-match-runs";

type Application = ListingRankingApplicationsQuery["listingApplications"]["nodes"][number];

export function rankingApplicationStates(applications: readonly Application[]) {
  const states = new Map<string, OvertakingApplicationState | null>();
  const contacts = new Set<string>();
  for (const application of applications) {
    const subject = application.companyId ?? application.person?.id;
    if (!subject) continue;
    const contact = `${subject}:${application.person?.id ?? application.id}`;
    if (contacts.has(contact)) continue;
    contacts.add(contact);
    const state = application.state === ListingApplicationState.Withdrawn || application.state === ListingApplicationState.Rejected
      ? application.state : null;
    // Newest application per contact; a company remains available while any contact still applies.
    if (!states.has(subject) || state === null) states.set(subject, state);
  }
  return states;
}

export function useListingRankingApplicationStates(listingId?: string) {
  const input = useMemo(() => ({ where: { listingId: { eq: listingId ?? "" } }, order: { receivedOn: SortOrder.Desc } }), [listingId]);
  const query = usePagedQuery<Application>(ListingRankingApplicationsDocument, {
    connectionKey: "listingApplications", input, pageSize: 100, skip: !listingId,
  });
  const { hasMore, loading, loadingMore, loadMoreFailed, loadMore } = query;
  useEffect(() => {
    if (hasMore && !loading && !loadingMore && !loadMoreFailed) loadMore();
  }, [hasMore, loading, loadingMore, loadMoreFailed, loadMore]);
  const unavailable = !!query.error || loadMoreFailed;
  const states = useMemo(() => rankingApplicationStates(listingId && !unavailable && !loading && !hasMore ? query.nodes : []),
    [listingId, unavailable, loading, hasMore, query.nodes]);
  return { states, unavailable };
}
